/** Shared checkout promotion calculation. Monetary values are integer satang. */
interface CartRow {
  productId?: string;
  productUnitId?: string;
  barcode: string;
  name: string;
  price: number;
  qty: number;
  free?: boolean;
  manualDiscount?: { unitAmount: number };
  lineDiscount?: number;
}
type ProductCategory = string;
export const cartRowManualDiscountTotal = (
  row: Pick<CartRow, 'qty' | 'manualDiscount' | 'lineDiscount'>,
): number =>
  row.manualDiscount
    ? row.manualDiscount.unitAmount * row.qty
    : (row.lineDiscount ?? 0);

export const cartRowNetAmount = (
  row: Pick<CartRow, 'qty' | 'price' | 'manualDiscount' | 'lineDiscount'>,
): number => Math.max(0, row.qty * row.price - cartRowManualDiscountTotal(row));

export type PromoRule = (
  | {
      kind: 'bill-amount';
      /** Minimum cart subtotal to trigger */
      minSubtotal: number;
      /** Flat baht discount */
      discountBaht: number;
    }
  | {
      kind: 'bill-percent';
      minSubtotal: number;
      discountPercent: number;
      maxDiscount?: number;
    }
  | {
      kind: 'item-bogo';
      /** Trigger barcode */
      barcode: string;
      buyQty: number;
      freeQty: number;
      perBillLimit?: number;
    }
  | {
      kind: 'item-fixed-price';
      /** Trigger barcode */
      barcode: string;
      fixedPrice: number;
      minQty: number;
      perBillLimit?: number;
    }
  | {
      kind: 'item-qty-tier';
      /** Eligible product barcodes; quantities are combined across these rows. */
      barcodes: string[];
      qtyPerTier: number;
      discountPerTier: number;
      /** Maximum number of discount tiers per bill. */
      perBillLimit?: number;
    }
  | {
      kind: 'item-percent';
      /** Match by exact barcode OR category (at least one required) */
      barcode?: string;
      category?: ProductCategory;
      minQty: number;
      discountPercent: number;
    }
) & {
  /** Additional purchase conditions, applied before any discount. */
  minSubtotal?: number;
  minQty?: number;
  /** Eligible units (free units for BOGO, tiers for qty-tier). */
  perBillLimit?: number;
  /** Full target set; singular barcode remains supported for local rules. */
  barcodes?: string[];
  /** Resolved primary selling units; an empty array deliberately matches none. */
  productUnitIds?: string[];
  productIds?: string[];
};

/** Canonical unit identity wins over barcode aliases. Legacy rows without
 * canonical IDs can still use resolved barcodes from the same unit.
 */
export function matchesPromotionTarget(
  rule: PromoRule,
  row: Pick<CartRow, 'barcode' | 'productId' | 'productUnitId' | 'free'>,
): boolean {
  if (row.free) return false;
  if (rule.productUnitIds !== undefined) {
    if (rule.productUnitIds.length === 0) return false;
    if (row.productUnitId) {
      return (
        rule.productUnitIds.includes(row.productUnitId) &&
        (!row.productId ||
          !rule.productIds ||
          rule.productIds.includes(row.productId))
      );
    }
    if (row.productId) return false;
  }
  if (rule.barcodes) return rule.barcodes.includes(row.barcode);
  if ('barcode' in rule && rule.barcode) return rule.barcode === row.barcode;
  if (rule.kind === 'item-percent') return !!rule.category;
  return !('barcode' in rule);
}

export interface Promotion {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  /** Optional date range — unix ms */
  startDate?: number;
  endDate?: number;
  rule: PromoRule;
}

export interface AppliedPromo {
  promo: Promotion;
  /** Total baht discount applied to the cart */
  discountBaht: number;
  /** Number of free units granted by a BOGO promotion. */
  freeQuantity?: number;
  /** Human-readable detail (used in display) */
  detail: string;
}

type DesktopPromotionRow = {
  id: unknown;
  name: unknown;
  active: unknown;
  start_date?: unknown;
  end_date?: unknown;
  type?: unknown;
  value?: unknown;
  min_baht?: unknown;
  min_qty?: unknown;
  per_bill_limit?: unknown;
  product_ids?: unknown;
  product_barcodes?: unknown;
  product_unit_ids?: unknown;
};

const parseStringArray = (value: unknown): string[] => {
  try {
    const parsed = Array.isArray(value)
      ? value
      : JSON.parse(String(value ?? '[]'));
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const parsePromotionDate = (
  value: unknown,
  boundary: 'start' | 'end',
  timezoneOffset?: string,
): number | undefined => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    if (timezoneOffset) {
      return Date.parse(
        `${value.trim()}T${boundary === 'end' ? '23:59:59.999' : '00:00:00.000'}${timezoneOffset}`,
      );
    }
    const [, year, month, day] = dateOnly;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      boundary === 'end' ? 23 : 0,
      boundary === 'end' ? 59 : 0,
      boundary === 'end' ? 59 : 0,
      boundary === 'end' ? 999 : 0,
    ).getTime();
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const mapDesktopPromotionRow = (
  row: DesktopPromotionRow,
  timezoneOffset = '+07:00',
): Promotion => {
  const type = String(row.type ?? '');
  const value = Number(row.value ?? 0);
  const minQty = Math.max(1, Number(row.min_qty ?? 1));
  const perBillLimit = Number(row.per_bill_limit);
  const barcodes = parseStringArray(row.product_barcodes);
  const legacyTargets = parseStringArray(row.product_ids);
  const barcode = barcodes[0] ?? legacyTargets[0] ?? '';
  const limit =
    Number.isFinite(perBillLimit) && perBillLimit > 0
      ? perBillLimit
      : undefined;

  let rule: PromoRule;
  if (type === 'amount_off') {
    rule = {
      kind: 'bill-amount',
      minSubtotal: Math.max(0, Number(row.min_baht ?? 0)),
      discountBaht: Math.max(0, value),
      minQty,
      ...(barcode
        ? { barcodes: barcodes.length ? barcodes : legacyTargets }
        : {}),
    };
  } else if (type === 'fixed_price') {
    rule = {
      kind: 'item-fixed-price',
      barcode,
      fixedPrice: Math.max(0, value),
      minQty,
      perBillLimit: limit,
    };
  } else if (type === 'percent_off') {
    rule = barcode
      ? {
          kind: 'item-percent',
          barcode,
          minQty,
          discountPercent: Math.max(0, value),
        }
      : {
          kind: 'bill-percent',
          minSubtotal: Math.max(0, Number(row.min_baht ?? 0)),
          discountPercent: Math.max(0, value),
        };
  } else if (type === 'bogo') {
    const freeQty = Math.max(1, Math.floor(value || 1));
    rule = {
      kind: 'item-bogo',
      barcode,
      buyQty: Math.max(1, minQty - freeQty),
      freeQty,
      perBillLimit: limit,
    };
  } else if (type === 'qty_tier') {
    rule = {
      kind: 'item-qty-tier',
      barcodes: barcodes.length > 0 ? barcodes : legacyTargets,
      qtyPerTier: minQty,
      discountPerTier: Math.max(0, value),
      perBillLimit: limit,
    };
  } else if (type === 'bill_percent') {
    rule = {
      kind: 'bill-percent',
      minSubtotal: Math.max(0, Number(row.min_baht ?? 0)),
      discountPercent: Math.max(0, value),
    };
  } else if (type === 'bill-amount') {
    rule = {
      kind: 'bill-amount',
      minSubtotal: Math.max(0, Number(row.min_baht ?? 0)),
      discountBaht: Math.max(0, value),
    };
  } else if (type === 'bill-percent') {
    rule = {
      kind: 'bill-percent',
      minSubtotal: Math.max(0, Number(row.min_baht ?? 0)),
      discountPercent: Math.max(0, value),
    };
  } else if (type === 'item-bogo') {
    rule = {
      kind: 'item-bogo',
      barcode,
      buyQty: minQty,
      freeQty: 1,
      perBillLimit: limit,
    };
  } else {
    rule = {
      kind: 'item-percent',
      barcode: barcode || undefined,
      category: undefined,
      minQty,
      discountPercent: Math.max(0, value),
    };
  }

  // Keep all selected targets and conditions on every synced rule.
  if (row.product_unit_ids !== undefined && legacyTargets.length > 0) {
    rule.productIds = legacyTargets;
    rule.productUnitIds = parseStringArray(row.product_unit_ids);
  }
  if (barcodes.length > 1 && rule.kind !== 'item-qty-tier') {
    rule.barcodes = barcodes;
  } else if (barcodes.length === 0 && legacyTargets.length > 1) {
    rule.barcodes = legacyTargets;
  }
  if (Number(row.min_baht) > 0) rule.minSubtotal = Number(row.min_baht);
  if (minQty > 1 && rule.kind !== 'item-bogo' && rule.kind !== 'item-qty-tier')
    rule.minQty = minQty;
  if (limit !== undefined) {
    if (type === 'bill_percent' && rule.kind === 'bill-percent') {
      rule.maxDiscount = limit;
    } else {
      rule.perBillLimit = limit;
    }
  }

  return {
    id: String(row.id),
    name: String(row.name),
    // Unknown future types must never become a percentage discount.
    active:
      Boolean(row.active) &&
      [
        'amount_off',
        'fixed_price',
        'percent_off',
        'bogo',
        'qty_tier',
        'bill_percent',
        'bill-amount',
        'bill-percent',
        'item-bogo',
        'item-percent',
      ].includes(type),
    startDate: parsePromotionDate(row.start_date, 'start', timezoneOffset),
    endDate: parsePromotionDate(row.end_date, 'end', timezoneOffset),
    rule,
  };
};

/** Filter promos that are active AND within their date range (if any) */
export function getActivePromos(
  promos: Promotion[],
  at: number = Date.now(),
): Promotion[] {
  return promos.filter((p) => {
    if (!p.active) return false;
    if (p.startDate && at < p.startDate) return false;
    if (p.endDate && at > p.endDate) return false;
    return true;
  });
}

/** Normalize equivalent cart layouts before rounding any pooled discount. */
function groupPromotionRows<T extends CartRow>(
  inputRows: T[],
  categoryOf: (barcode: string, row?: T) => ProductCategory | undefined,
  promos: Promotion[],
): T[] {
  const rows: T[] = [];
  const byKey = new Map<string, T>();
  for (const row of inputRows) {
    const key = JSON.stringify([
      row.productId,
      row.productUnitId,
      row.productUnitId ? undefined : row.barcode,
      row.name,
      row.price,
      !!row.free,
      row.manualDiscount?.unitAmount,
      categoryOf(row.barcode, row),
      // Canonical aliases are equivalent only when every active rule agrees.
      // Preserve legacy rules that intentionally target an individual barcode.
      promos.map((promo) => matchesPromotionTarget(promo.rule, row)),
      cartRowNetAmount(row) / Math.max(1, row.qty),
    ]);
    const existing = byKey.get(key);
    if (existing) {
      existing.qty += row.qty;
      if (row.lineDiscount !== undefined) {
        existing.lineDiscount = (existing.lineDiscount ?? 0) + row.lineDiscount;
      }
    } else {
      const copy = { ...row };
      byKey.set(key, copy);
      rows.push(copy);
    }
  }
  return rows;
}

/** Compute all matching promotions and their discount amounts for a given cart. */
export function computeAppliedPromos<T extends CartRow>(
  inputRows: T[],
  rowsSubtotal: number,
  promos: Promotion[],
  categoryOf: (barcode: string, row?: T) => ProductCategory | undefined,
  at: number = Date.now(),
): AppliedPromo[] {
  const applied: AppliedPromo[] = [];
  const activePromos = getActivePromos(promos, at);
  // Split only at discount boundaries; large quantities stay compact while
  // discounted, full-price and already-free units retain their own value.
  const rows = groupPromotionRows(inputRows, categoryOf, activePromos);
  const remainingByRow = rows.map((row) => cartRowNetAmount(row));
  const splitRow = (index: number, qty: number) => {
    const row = rows[index]!;
    if (qty <= 0 || qty >= row.qty) return;
    const available = remainingByRow[index] ?? 0;
    const selectedAmount = Math.round((available * qty) / row.qty);
    rows.push({ ...row, qty: row.qty - qty });
    remainingByRow.push(available - selectedAmount);
    rows[index] = { ...row, qty };
    remainingByRow[index] = selectedAmount;
  };
  const compareRows = (a: number, b: number) => {
    const left = rows[a]!,
      right = rows[b]!;
    const compareText = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
    return (
      compareText(
        left.productId ?? left.productUnitId ?? left.barcode,
        right.productId ?? right.productUnitId ?? right.barcode,
      ) ||
      compareText(left.productUnitId ?? '', right.productUnitId ?? '') ||
      left.price - right.price ||
      (remainingByRow[a] ?? 0) / Math.max(1, left.qty) -
        (remainingByRow[b] ?? 0) / Math.max(1, right.qty) ||
      left.qty - right.qty ||
      compareText(left.name, right.name) ||
      compareText(
        categoryOf(left.barcode, left) ?? '',
        categoryOf(right.barcode, right) ?? '',
      ) ||
      compareText(
        JSON.stringify(
          activePromos.map((promo) => matchesPromotionTarget(promo.rule, left)),
        ),
        JSON.stringify(
          activePromos.map((promo) =>
            matchesPromotionTarget(promo.rule, right),
          ),
        ),
      ) ||
      compareText(left.barcode, right.barcode)
    );
  };
  const remainingFor = (indexes: readonly number[]) =>
    indexes.reduce((sum, index) => sum + (remainingByRow[index] ?? 0), 0);
  const consume = (
    indexes: readonly number[],
    requested: number,
    weights?: ReadonlyMap<number, number>,
  ) => {
    const entries = indexes
      .map((index) => ({
        index,
        available: remainingByRow[index] ?? 0,
        weight: weights
          ? (weights.get(index) ?? 0)
          : (remainingByRow[index] ?? 0),
      }))
      .filter((entry) => entry.available > 0 && entry.weight > 0);
    const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);
    if (totalWeight <= 0) return 0;
    const amount = Math.min(
      requested,
      entries.reduce((sum, entry) => sum + entry.available, 0),
    );
    const shares = entries.map((entry) => {
      const exact = (entry.weight / totalWeight) * amount;
      return {
        ...entry,
        allocated: Math.min(entry.available, Math.floor(exact)),
        fraction: exact - Math.floor(exact),
      };
    });
    let remainder =
      amount - shares.reduce((sum, share) => sum + share.allocated, 0);
    // Keep integer-satang rounding deterministic, including tied remainders.
    shares.sort(
      (a, b) => b.fraction - a.fraction || compareRows(a.index, b.index),
    );
    for (const share of shares) {
      if (remainder > 0 && share.allocated < share.available) {
        share.allocated += 1;
        remainder -= 1;
      }
      remainingByRow[share.index] = share.available - share.allocated;
    }
    return amount - remainder;
  };

  for (const promo of activePromos) {
    // A rounded pooled discount can leave units one satang apart. Preserve
    // those prices too, so later BOGO picks a whole cheapest unit.
    for (let index = 0, count = rows.length; index < count; index++) {
      const qty = rows[index]!.qty;
      const available = remainingByRow[index] ?? 0;
      if (!Number.isInteger(qty) || qty <= 1) continue;
      const remainder = available % qty;
      if (!remainder) continue;
      const unitPrice = Math.floor(available / qty);
      splitRow(index, qty - remainder);
      remainingByRow[index] = unitPrice * (qty - remainder);
      remainingByRow[remainingByRow.length - 1] = (unitPrice + 1) * remainder;
    }
    const { rule } = promo;
    if (rowsSubtotal < (rule.minSubtotal ?? 0)) continue;
    let discount = 0;
    const discountsByRow = new Map<number, number>();
    const addDiscount = (index: number, amount: number) => {
      if (amount <= 0) return;
      discountsByRow.set(index, amount);
      discount += amount;
    };
    let freeQuantity: number | undefined;
    let detail = '';
    let eligibleRowIndexes = rows
      .map((_, index) => index)
      .filter((index) => matchesPromotionTarget(rule, rows[index]!));
    if (rule.kind === 'item-percent' && rule.category) {
      eligibleRowIndexes = eligibleRowIndexes.filter(
        (index) =>
          categoryOf(rows[index]!.barcode, rows[index]) === rule.category,
      );
    }
    // Shared quantity limits must select the same units for every scan order.
    eligibleRowIndexes.sort(compareRows);
    const eligibleQty = eligibleRowIndexes.reduce(
      (sum, index) => sum + rows[index]!.qty,
      0,
    );
    if (eligibleQty < (rule.minQty ?? 0)) continue;

    const limitedAmounts = () => {
      let quantityLeft = rule.perBillLimit ?? Number.POSITIVE_INFINITY;
      const amounts = new Map<number, number>();
      for (const index of eligibleRowIndexes) {
        const row = rows[index]!;
        const qty = Math.min(row.qty, quantityLeft);
        splitRow(index, qty);
        amounts.set(index, qty > 0 ? (remainingByRow[index] ?? 0) : 0);
        quantityLeft -= qty;
      }
      return amounts;
    };

    if (rule.kind === 'bill-amount') {
      if (rowsSubtotal >= rule.minSubtotal) {
        discount = rule.discountBaht;
        detail = `ซื้อครบ ฿${(rule.minSubtotal / 100).toLocaleString()} ลด ฿${(rule.discountBaht / 100).toLocaleString()}`;
      }
    } else if (rule.kind === 'bill-percent') {
      if (rowsSubtotal >= rule.minSubtotal) {
        if (rule.perBillLimit !== undefined || rule.barcodes) {
          for (const [index, amount] of limitedAmounts())
            addDiscount(index, (amount * rule.discountPercent) / 100);
        } else {
          discount = (rowsSubtotal * rule.discountPercent) / 100;
        }
        if (rule.maxDiscount !== undefined)
          discount = Math.min(discount, rule.maxDiscount);
        detail = `ซื้อครบ ฿${(rule.minSubtotal / 100).toLocaleString()} ลด ${rule.discountPercent}%`;
      }
    } else if (rule.kind === 'item-bogo') {
      if (eligibleQty >= rule.buyQty) {
        const bundleSize = rule.buyQty + rule.freeQty;
        const sets = Math.floor(eligibleQty / bundleSize);
        const freeUnits = Math.min(
          sets * rule.freeQty,
          rule.perBillLimit ?? Number.POSITIVE_INFINITY,
        );
        if (freeUnits > 0) {
          let unitsLeft = freeUnits;
          const byUnitPrice = [...eligibleRowIndexes].sort(
            (a, b) =>
              (remainingByRow[a] ?? 0) / Math.max(1, rows[a]!.qty) -
                (remainingByRow[b] ?? 0) / Math.max(1, rows[b]!.qty) ||
              compareRows(a, b),
          );
          // Allocate the discount to the cheapest free rows so later promotions
          // see the value actually remaining on each product.
          eligibleRowIndexes = byUnitPrice;
          for (const index of byUnitPrice) {
            const row = rows[index]!;
            const qty = Math.min(row.qty, unitsLeft);
            splitRow(index, qty);
            addDiscount(index, qty > 0 ? (remainingByRow[index] ?? 0) : 0);
            unitsLeft -= qty;
          }
          freeQuantity = freeUnits;
          detail = `${promo.name} ซื้อ ${rule.buyQty} แถม ${rule.freeQty} (${sets} ชุด)`;
        }
      }
    } else if (rule.kind === 'item-fixed-price') {
      if (eligibleQty >= rule.minQty) {
        let quantityLeft = rule.perBillLimit ?? Number.POSITIVE_INFINITY;
        for (const index of eligibleRowIndexes) {
          const row = rows[index]!;
          const discountedQty = Math.min(row.qty, quantityLeft);
          const effectiveUnitPrice =
            (remainingByRow[index] ?? 0) / Math.max(1, row.qty);
          if (effectiveUnitPrice <= rule.fixedPrice) continue;
          splitRow(index, discountedQty);
          addDiscount(
            index,
            (effectiveUnitPrice - rule.fixedPrice) * discountedQty,
          );
          quantityLeft -= discountedQty;
        }
        detail = `${promo.name} ราคาพิเศษ ฿${(rule.fixedPrice / 100).toLocaleString()}`;
      }
    } else if (rule.kind === 'item-qty-tier') {
      const availableTiers = Math.floor(eligibleQty / rule.qtyPerTier);
      const appliedTiers = Math.min(
        availableTiers,
        rule.perBillLimit ?? Number.POSITIVE_INFINITY,
      );
      if (appliedTiers > 0) {
        const eligibleAmount = remainingFor(eligibleRowIndexes);
        discount = Math.min(
          eligibleAmount,
          appliedTiers * rule.discountPerTier,
        );
        detail = `ครบทุก ${rule.qtyPerTier} ชิ้น ลด ฿${(rule.discountPerTier / 100).toLocaleString()} (${appliedTiers} ชุด)`;
      }
    } else if (rule.kind === 'item-percent') {
      if (eligibleQty >= rule.minQty) {
        for (const [index, amount] of limitedAmounts())
          addDiscount(index, (amount * rule.discountPercent) / 100);
        const target = rule.category ?? rule.barcode ?? 'รายการที่กำหนด';
        detail = `ลด ${rule.discountPercent}% (${target}, ${eligibleQty} ชิ้น)`;
      }
    }

    if (discount > 0) {
      const requestedDiscount = Math.min(
        Math.round(discount),
        remainingFor(eligibleRowIndexes),
      );
      if (requestedDiscount <= 0) continue;
      const discountBaht = consume(
        eligibleRowIndexes,
        requestedDiscount,
        discountsByRow.size ? discountsByRow : undefined,
      );
      if (discountBaht <= 0) continue;
      applied.push({
        promo,
        discountBaht,
        ...(freeQuantity !== undefined ? { freeQuantity } : {}),
        detail,
      });
    }
  }

  return applied;
}
